import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Save, CheckCircle2, Gavel, Ban, Minus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const resultOptions = [
  { value: 'pending', label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  { value: 'sold', label: 'Sold', className: 'bg-green-100 text-green-700' },
  { value: 'rna', label: 'RNA', className: 'bg-yellow-100 text-yellow-700' },
  { value: 'withdrawn', label: 'Withdrawn', className: 'bg-red-100 text-red-700' },
];

const AuctionResultsTab = ({ saleOpportunities, saleSessions, onRefresh }) => {
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const getEdit = (oppId, field) => edits[oppId]?.[field];
  const getValue = (opp, field) => getEdit(opp.id, field) ?? opp[field] ?? '';
  const isDirty = (oppId) => edits[oppId] && Object.keys(edits[oppId]).length > 0;

  const updateField = (oppId, field, value) => {
    setEdits(prev => ({
      ...prev,
      [oppId]: { ...prev[oppId], [field]: value }
    }));
  };

  const handleSaveRow = async (opp) => {
    if (!isDirty(opp.id)) return;
    setSaving(true);
    try {
      const data = { ...edits[opp.id] };
      if (data.sale_price !== undefined) data.sale_price = parseFloat(data.sale_price) || null;
      if (data.rna_hammer_price !== undefined) data.rna_hammer_price = parseFloat(data.rna_hammer_price) || null;
      await api.put(`/sale-opportunities/${opp.id}`, data);
      setEdits(prev => { const next = { ...prev }; delete next[opp.id]; return next; });
      toast.success(`Updated ${opp.opportunity_name?.split(' - ')[1] || 'opportunity'}`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  const handleBulkResult = async (result) => {
    if (selected.size === 0) return;
    setSaving(true);
    let success = 0;
    for (const oppId of selected) {
      try {
        await api.put(`/sale-opportunities/${oppId}`, { result });
        success++;
      } catch { /* continue */ }
    }
    toast.success(`Updated ${success} opportunit${success === 1 ? 'y' : 'ies'} to ${result.toUpperCase()}`);
    setSelected(new Set());
    setEdits({});
    if (onRefresh) onRefresh();
    setSaving(false);
  };

  const toggleSelect = (oppId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(oppId) ? next.delete(oppId) : next.add(oppId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === saleOpportunities.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(saleOpportunities.map(o => o.id)));
    }
  };

  const horseName = (opp) => opp.opportunity_name?.split(' - ')[1] || '-';

  if (saleOpportunities.length === 0) {
    return <p className="text-sm text-muted-foreground italic py-4">No horses entered in this sale</p>;
  }

  const resultBadge = (value) => {
    const opt = resultOptions.find(o => o.value === value);
    if (!opt) return <span className="text-xs text-muted-foreground">Pending</span>;
    return <Badge className={opt.className}>{opt.label}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded border">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleBulkResult('sold')} disabled={saving}
              className="text-green-700 border-green-300 hover:bg-green-50">
              <Gavel className="w-3 h-3 mr-1" /> Sold
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkResult('rna')} disabled={saving}
              className="text-yellow-700 border-yellow-300 hover:bg-yellow-50">
              <Minus className="w-3 h-3 mr-1" /> RNA
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkResult('withdrawn')} disabled={saving}
              className="text-red-700 border-red-300 hover:bg-red-50">
              <Ban className="w-3 h-3 mr-1" /> Withdrawn
            </Button>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="ml-auto text-xs">
            Clear
          </Button>
        </div>
      )}

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={selected.size === saleOpportunities.length}
                onCheckedChange={toggleAll}
              />
            </TableHead>
            <TableHead>Hip #</TableHead>
            <TableHead>Horse</TableHead>
            <TableHead>Session</TableHead>
            <TableHead>Result</TableHead>
            <TableHead>Sale Price</TableHead>
            <TableHead>RNA Price</TableHead>
            <TableHead>Withdrawn Date</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className="w-16"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {saleOpportunities.map(opp => {
            const session = saleSessions.find(s => s.id === opp.session_id);
            const dirty = isDirty(opp.id);
            const currentResult = getValue(opp, 'result');

            return (
              <TableRow key={opp.id} className={dirty ? 'bg-amber-50/50' : ''}>
                <TableCell>
                  <Checkbox
                    checked={selected.has(opp.id)}
                    onCheckedChange={() => toggleSelect(opp.id)}
                  />
                </TableCell>
                <TableCell className="font-medium">#{opp.hip_number || 'TBD'}</TableCell>
                <TableCell className="font-medium">{horseName(opp)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {session?.date ? new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                </TableCell>
                <TableCell>
                  <Select
                    value={currentResult || 'pending'}
                    onValueChange={(v) => updateField(opp.id, 'result', v)}
                  >
                    <SelectTrigger className="h-8 w-32 text-xs">
                      <SelectValue>
                        {resultBadge(currentResult || 'pending')}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {resultOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {currentResult === 'sold' ? (
                    <Input type="number" className="h-8 w-28 text-xs" placeholder="$0" value={getValue(opp, 'sale_price')} onChange={(e) => updateField(opp.id, 'sale_price', e.target.value)} />
                  ) : <span className="text-xs text-muted-foreground">-</span>}
                </TableCell>
                <TableCell>
                  {currentResult === 'rna' ? (
                    <Input type="number" className="h-8 w-28 text-xs" placeholder="$0" value={getValue(opp, 'rna_hammer_price')} onChange={(e) => updateField(opp.id, 'rna_hammer_price', e.target.value)} />
                  ) : <span className="text-xs text-muted-foreground">-</span>}
                </TableCell>
                <TableCell>
                  {currentResult === 'withdrawn' ? (
                    <Input
                      type="date"
                      className={`h-8 w-36 text-xs ${getValue(opp, 'withdrawal_date') ? 'date-filled' : ''}`}
                      value={getValue(opp, 'withdrawal_date')}
                      onChange={(e) => updateField(opp.id, 'withdrawal_date', e.target.value)}
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {currentResult === 'withdrawn' ? (
                    <Input
                      className="h-8 w-36 text-xs"
                      placeholder="Reason"
                      value={getValue(opp, 'withdrawal_reason')}
                      onChange={(e) => updateField(opp.id, 'withdrawal_reason', e.target.value)}
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {dirty && (
                    <Button size="sm" variant="ghost" onClick={() => handleSaveRow(opp)} disabled={saving} className="h-7 w-7 p-0">
                      <Save className="w-4 h-4 text-primary" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default AuctionResultsTab;
